import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Flame, Eye, TrendingUp, ShieldCheck, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

/** ISO2 lookup for flag rendering — mirror of CountrySpotlights. */
const COUNTRY_ISO = {
  Algeria: "DZ", Angola: "AO", Benin: "BJ", Botswana: "BW", "Burkina Faso": "BF", Burundi: "BI",
  Cameroon: "CM", "Cape Verde": "CV", "Central African Republic": "CF", Chad: "TD", Comoros: "KM",
  Congo: "CG", "DR Congo": "CD", Djibouti: "DJ", Egypt: "EG", "Equatorial Guinea": "GQ",
  Eritrea: "ER", Eswatini: "SZ", Ethiopia: "ET", Gabon: "GA", Gambia: "GM", Ghana: "GH",
  Guinea: "GN", "Guinea-Bissau": "GW", "Ivory Coast": "CI", Kenya: "KE", Lesotho: "LS",
  Liberia: "LR", Libya: "LY", Madagascar: "MG", Malawi: "MW", Mali: "ML", Mauritania: "MR",
  Mauritius: "MU", Morocco: "MA", Mozambique: "MZ", Namibia: "NA", Niger: "NE", Nigeria: "NG",
  Rwanda: "RW", "Sao Tome and Principe": "ST", Senegal: "SN", Seychelles: "SC",
  "Sierra Leone": "SL", Somalia: "SO", "South Africa": "ZA", "South Sudan": "SS", Sudan: "SD",
  Tanzania: "TZ", Togo: "TG", Tunisia: "TN", Uganda: "UG", Zambia: "ZM", Zimbabwe: "ZW",
};

function flag(name) {
  const iso2 = COUNTRY_ISO[name];
  if (!iso2) return "🌍";
  return String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
}

const rankColors = ["#eab308", "#c0c0c0", "#cd7f32"]; // gold / silver / bronze

/**
 * TrendingByNation - top 3 most-viewed verified players per country over last 7 days.
 * Props:
 *   compact (bool): renders slim horizontal-scroll variant for Scout Dashboard.
 */
export default function TrendingByNation({ compact = false }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trending/nations?top=3&days=7")
      .then((r) => setData(r.data || []))
      .catch((err) => console.debug("trending failed", err?.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading || data.length === 0) return null;

  const posterUrl = (country) => `${(process.env.REACT_APP_BACKEND_URL || "")}/api/trending/poster/${encodeURIComponent(country)}.png`;

  if (compact) {
    return (
      <section className="border border-[#e65f2b]/40 bg-gradient-to-r from-[#1a0f08] via-black to-[#1a0f08] p-5" data-testid="trending-by-nation-compact">
        <div className="flex items-center gap-2 mb-4">
          <Flame size={16} className="text-[#e65f2b]" />
          <div className="font-display uppercase text-sm font-bold tracking-widest">{t("trending.overline")}</div>
          <div className="text-[10px] text-white/50 uppercase tracking-widest ml-2">{t("trending.mostViewed")}</div>
        </div>
        <div className="flex overflow-x-auto gap-4 pb-1 -mx-1 px-1 snap-x">
          {data.slice(0, 6).map((group) => (
            <div key={group.country} className="shrink-0 w-72 snap-start border border-white/10 bg-black/50 p-3" data-testid={`trending-country-${group.country}`}>
              <div className="flex items-center gap-2 pb-2 border-b border-white/10 mb-2">
                <span className="text-xl leading-none">{flag(group.country)}</span>
                <span className="font-display uppercase text-sm font-bold tracking-widest truncate">{group.country}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><Eye size={10} />{group.total_views}</span>
              </div>
              <div className="space-y-2">
                {group.players.map((p, i) => (
                  <Link
                    key={p.user_id}
                    to={`/players/${p.user_id}`}
                    data-testid={`trending-player-${p.user_id}`}
                    className="flex items-center gap-3 hover:bg-white/5 p-1 -m-1 transition-colors group"
                  >
                    <span className="font-display text-lg font-bold" style={{ color: rankColors[i] || "#666" }}>{i + 1}</span>
                    <div className="w-10 h-10 bg-black shrink-0 overflow-hidden">
                      {p.photo_path
                        ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">{(p.name || "?")[0]}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold truncate group-hover:text-[#eab308] flex items-center gap-1">{p.name}<ShieldCheck size={10} className="text-[#eab308]" /></div>
                      <div className="text-[10px] text-white/50 uppercase">{p.position || "—"} · {p.age ? `${p.age}y` : "—"}</div>
                    </div>
                    <span className="text-[10px] text-white/40 flex items-center gap-0.5"><Eye size={9} />{p.views}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Landing page — full section
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="trending-by-nation-section">
      <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] flex items-center gap-2">
            <Flame size={12} /> {t("trending.overline")}
          </div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
            {t("trending.titleA")} <span className="text-[#eab308]">{t("trending.titleB")}</span>.
          </h2>
        </div>
        <p className="md:col-span-4 text-white/70">
          {t("trending.subtitle")}
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.slice(0, 9).map((group) => (
          <div key={group.country} className="border border-white/10 bg-[#141414] p-5" data-testid={`trending-card-${group.country}`}>
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-white/10">
              <span className="text-3xl leading-none">{flag(group.country)}</span>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase text-lg font-bold tracking-tight truncate">{group.country}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1 mt-0.5">
                  <TrendingUp size={10} /> {t("common.viewsCount", { count: group.total_views })}
                </div>
              </div>
              <a
                href={posterUrl(group.country)}
                target="_blank"
                rel="noopener noreferrer"
                download={`afrigoall-trending-${group.country.toLowerCase().replace(/\s+/g, "-")}.png`}
                title={t("trending.posterHint")}
                data-testid={`trending-poster-${group.country}`}
                className="shrink-0 w-8 h-8 border border-white/15 flex items-center justify-center hover:border-[#eab308] hover:text-[#eab308] transition-colors text-white/60"
              >
                <Download size={12} />
              </a>
            </div>
            <div className="space-y-3">
              {group.players.map((p, i) => (
                <Link
                  key={p.user_id}
                  to={`/players/${p.user_id}`}
                  data-testid={`trending-nation-player-${p.user_id}`}
                  className="flex items-center gap-3 group hover:bg-white/5 p-1 -m-1"
                >
                  <span className="font-display text-2xl font-bold w-8 text-center" style={{ color: rankColors[i] || "#666" }}>{i + 1}</span>
                  <div className="w-12 h-12 bg-black shrink-0 overflow-hidden">
                    {p.photo_path
                      ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-white/30 text-xl">{(p.name || "?")[0]}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold uppercase truncate group-hover:text-[#eab308] flex items-center gap-1.5">
                      {p.name}
                      <ShieldCheck size={11} className="text-[#eab308]" />
                    </div>
                    <div className="text-[10px] text-white/50 uppercase tracking-widest mt-0.5">
                      {p.position || "—"} · {p.age ? `${p.age}y` : "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-white/40">
                    <Eye size={11} /> {p.views}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
